import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import DeleteIcon from 'assets/settings/components/DeleteIcon';
import EditIcon from 'assets/settings/components/EditIcon';
import Select from 'common-ui/select/Select';
import Pagination from 'common-ui/table/Pagination';
import {
  getAdministrativeDistricts,
  getAdministrativeRegions,
  getBuildings,
  getCities,
  getStreets,
} from 'features/newsales/newsalesActions';
import {
  selectBuildings,
  selectBuildingsCurrentPage,
  selectBuildingsDeleteSuccess,
  selectBuildingsLoading,
  selectBuildingsPagesCount,
  selectBuildingsSearchData,
  selectBuildingsSelectedAdministrativeDistrictId,
  selectBuildingsSelectedCityId,
  selectBuildingsSelectedCountryId,
  selectBuildingsSelectedDistrictId,
  selectBuildingsSelectedStreetId,
  selectBuildingsSuccess,
  selectBuildingsUpdateSuccess,
  setBuildingsCreateSuccess,
  setBuildingsCurrentPage,
  setBuildingsDeleteSuccess,
  setBuildingsSearchData,
  setBuildingsSelectedAdministrativeDistrict,
  setBuildingsSelectedCity,
  setBuildingsSelectedCountry,
  setBuildingsSelectedDistrict,
  setBuildingsSelectedStreet,
  setBuildingsUpdateSuccess,
} from 'features/newsales/newsalesSlice';

import {
  ActionsCell,
  EmptyState,
  FilterFlex,
  FilterSection,
  IconButton,
  LoadingSpinner,
  PaginationWrapper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableHeaderActions,
  TableRow,
} from '../Addresses.styles';

const BuildingPage = ({ onEdit, onDelete, allCountries }) => {
  const dispatch = useDispatch();
  const [regionsForBuildings, setRegionsForBuildings] = useState([]);
  const [citiesForBuildings, setCitiesForBuildings] = useState([]);
  const [streetsForBuildings, setStreetsForBuildings] = useState([]);
  const [administrativeDistrictsForBuildings, setAdministrativeDistrictsForBuildings] =
    useState([]);

  const buildings = useSelector(selectBuildings);
  const buildingsLoading = useSelector(selectBuildingsLoading);
  const buildingsCurrentPage = useSelector(selectBuildingsCurrentPage);
  const buildingsPagesCount = useSelector(selectBuildingsPagesCount);
  const buildingsSearchData = useSelector(selectBuildingsSearchData);
  const buildingsCreateSuccess = useSelector(selectBuildingsSuccess);
  const buildingsUpdateSuccess = useSelector(selectBuildingsUpdateSuccess);
  const buildingsDeleteSuccess = useSelector(selectBuildingsDeleteSuccess);
  const buildingSelectedCountryId = useSelector(selectBuildingsSelectedCountryId);
  const buildingSelectedDistrictId = useSelector(selectBuildingsSelectedDistrictId);
  const buildingSelectedCityId = useSelector(selectBuildingsSelectedCityId);
  const buildingSelectedStreetId = useSelector(selectBuildingsSelectedStreetId);
  const buildingSelectedAdministrativeDistrictId = useSelector(
    selectBuildingsSelectedAdministrativeDistrictId
  );

  useEffect(() => {
    if (buildingSelectedCountryId) {
      dispatch(
        getAdministrativeRegions({
          countryId: buildingSelectedCountryId,
          limit: 1000,
        })
      ).then((result) => {
        if (result.payload && result.payload.data) {
          setRegionsForBuildings(result.payload.data);
        }
      });
    }
  }, [dispatch, buildingSelectedCountryId]);

  useEffect(() => {
    if (buildingSelectedCountryId && buildingSelectedDistrictId) {
      dispatch(
        getCities({
          countryId: buildingSelectedCountryId,
          districtId: buildingSelectedDistrictId,
          limit: 1000,
        })
      ).then((result) => {
        if (result.payload && result.payload.data) {
          setCitiesForBuildings(result.payload.data);
        }
      });
    }
  }, [dispatch, buildingSelectedCountryId, buildingSelectedDistrictId]);

  useEffect(() => {
    if (buildingSelectedCountryId && buildingSelectedDistrictId && buildingSelectedCityId) {
      dispatch(
        getAdministrativeDistricts({
          countryId: buildingSelectedCountryId,
          districtId: buildingSelectedDistrictId,
          cityId: buildingSelectedCityId,
          limit: 1000,
        })
      ).then((result) => {
        if (result.payload && result.payload.data) {
          setAdministrativeDistrictsForBuildings(result.payload.data);
        }
      });

      dispatch(
        getStreets({
          countryId: buildingSelectedCountryId,
          districtId: buildingSelectedDistrictId,
          cityId: buildingSelectedCityId,
          limit: 1000,
        })
      ).then((result) => {
        if (result.payload && result.payload.data) {
          setStreetsForBuildings(result.payload.data);
        }
      });
    }
  }, [
    dispatch,
    buildingSelectedCountryId,
    buildingSelectedDistrictId,
    buildingSelectedCityId,
  ]);

  useEffect(() => {
    if (
      buildingSelectedCountryId &&
      buildingSelectedDistrictId &&
      buildingSelectedCityId &&
      buildingSelectedStreetId
    ) {
      dispatch(
        getBuildings({
          countryId: buildingSelectedCountryId,
          districtId: buildingSelectedDistrictId,
          cityId: buildingSelectedCityId,
          streetId: buildingSelectedStreetId,
          administrativeDistrictId: buildingSelectedAdministrativeDistrictId,
          page: buildingsCurrentPage,
          limit: buildingsSearchData.limit,
          search: buildingsSearchData.search,
        })
      );
    }
  }, [
    dispatch,
    buildingSelectedCountryId,
    buildingSelectedDistrictId,
    buildingSelectedCityId,
    buildingSelectedStreetId,
    buildingSelectedAdministrativeDistrictId,
    buildingsCurrentPage,
    buildingsSearchData.limit,
    buildingsSearchData.search,
  ]);

  useEffect(() => {
    if (buildingsCreateSuccess) {
      dispatch(setBuildingsCreateSuccess(false));
      if (
        buildingSelectedCountryId &&
        buildingSelectedDistrictId &&
        buildingSelectedCityId &&
        buildingSelectedStreetId
      ) {
        dispatch(
          getBuildings({
            countryId: buildingSelectedCountryId,
            districtId: buildingSelectedDistrictId,
            cityId: buildingSelectedCityId,
            streetId: buildingSelectedStreetId,
            administrativeDistrictId: buildingSelectedAdministrativeDistrictId,
            page: buildingsCurrentPage,
            limit: buildingsSearchData.limit,
            search: buildingsSearchData.search,
          })
        );
      }
    }
  }, [
    buildingsCreateSuccess,
    dispatch,
    buildingSelectedCountryId,
    buildingSelectedDistrictId,
    buildingSelectedCityId,
    buildingSelectedStreetId,
    buildingSelectedAdministrativeDistrictId,
    buildingsCurrentPage,
    buildingsSearchData.limit,
    buildingsSearchData.search,
  ]);

  useEffect(() => {
    if (buildingsUpdateSuccess) {
      dispatch(setBuildingsUpdateSuccess(false));
    }
  }, [buildingsUpdateSuccess, dispatch]);

  useEffect(() => {
    if (buildingsDeleteSuccess) {
      dispatch(setBuildingsDeleteSuccess(false));
      if (buildings?.length === 1 && buildingsCurrentPage > 1) {
        dispatch(setBuildingsCurrentPage(buildingsCurrentPage - 1));
      } else {
        // Refetch current page data after deletion
        if (
          buildingSelectedCountryId &&
          buildingSelectedDistrictId &&
          buildingSelectedCityId &&
          buildingSelectedStreetId
        ) {
          dispatch(
            getBuildings({
              countryId: buildingSelectedCountryId,
              districtId: buildingSelectedDistrictId,
              cityId: buildingSelectedCityId,
              streetId: buildingSelectedStreetId,
              administrativeDistrictId: buildingSelectedAdministrativeDistrictId,
              page: buildingsCurrentPage,
              limit: buildingsSearchData.limit,
              search: buildingsSearchData.search,
            })
          );
        }
      }
    }
  }, [
    buildingsDeleteSuccess,
    dispatch,
    buildings,
    buildingsCurrentPage,
    buildingSelectedCountryId,
    buildingSelectedDistrictId,
    buildingSelectedCityId,
    buildingSelectedStreetId,
    buildingSelectedAdministrativeDistrictId,
    buildingsSearchData.limit,
    buildingsSearchData.search,
  ]);

  const handleBuildingCountrySelect = (selectedOption) => {
    const countryId = selectedOption ? selectedOption.value : null;
    dispatch(setBuildingsSelectedCountry(countryId));
  };

  const handleBuildingDistrictSelect = (selectedOption) => {
    const districtId = selectedOption ? selectedOption.value : null;
    dispatch(setBuildingsSelectedDistrict(districtId));
  };

  const handleBuildingCitySelect = (selectedOption) => {
    const cityId = selectedOption ? selectedOption.value : null;
    dispatch(setBuildingsSelectedCity(cityId));
  };

  const handleBuildingStreetSelect = (selectedOption) => {
    const streetId = selectedOption ? selectedOption.value : null;
    dispatch(setBuildingsSelectedStreet(streetId));
  };

  const handleBuildingAdministrativeDistrictSelect = (selectedOption) => {
    const administrativeDistrictId = selectedOption ? selectedOption.value : null;
    dispatch(setBuildingsSelectedAdministrativeDistrict(administrativeDistrictId));
  };

  const handlePageChange = (page) => {
    dispatch(setBuildingsCurrentPage(page));
  };

  const handleRowCountChange = (count) => {
    dispatch(setBuildingsSearchData({ limit: count, offset: 0 }));
    dispatch(setBuildingsCurrentPage(1));
  };

  const buildingCountryOptions = allCountries.map((country) => ({
    value: country.id,
    label: country.name,
  }));

  const buildingRegionOptions = regionsForBuildings.map((region) => ({
    value: region.id,
    label: region.name,
  }));

  const buildingCityOptions = citiesForBuildings.map((city) => ({
    value: city.id,
    label: city.name,
  }));

  const buildingStreetOptions = streetsForBuildings.map((street) => ({
    value: street.id,
    label: street.name,
  }));

  const buildingAdministrativeDistrictOptions = administrativeDistrictsForBuildings.map(
    (district) => ({
      value: district.id,
      label: district.name,
    })
  );

  const buildingSelectedCountryOption = buildingSelectedCountryId
    ? buildingCountryOptions.find((option) => option.value === buildingSelectedCountryId)
    : null;

  const buildingSelectedDistrictOption = buildingSelectedDistrictId
    ? buildingRegionOptions.find((option) => option.value === buildingSelectedDistrictId)
    : null;

  const buildingSelectedCityOption = buildingSelectedCityId
    ? buildingCityOptions.find((option) => option.value === buildingSelectedCityId)
    : null;

  const buildingSelectedStreetOption = buildingSelectedStreetId
    ? buildingStreetOptions.find((option) => option.value === buildingSelectedStreetId)
    : null;

  const buildingSelectedAdministrativeDistrictOption = buildingSelectedAdministrativeDistrictId
    ? buildingAdministrativeDistrictOptions.find(
        (option) => option.value === buildingSelectedAdministrativeDistrictId
      )
    : null;

  return (
    <>
      <FilterFlex>
        <FilterSection $noBorder>
          <Select
            label="Select Country"
            value={buildingSelectedCountryOption}
            onChange={handleBuildingCountrySelect}
            options={buildingCountryOptions}
            placeholder="Select a country"
            isClearable
            isDisabled={allCountries.length === 0}
          />
        </FilterSection>
        <FilterSection $noBorder>
          <Select
            label="Select Region"
            value={buildingSelectedDistrictOption}
            onChange={handleBuildingDistrictSelect}
            options={buildingRegionOptions}
            placeholder="Select a region"
            isClearable
            isDisabled={!buildingSelectedCountryId || regionsForBuildings.length === 0}
          />
        </FilterSection>
        <FilterSection $noBorder>
          <Select
            label="Select City/Village"
            value={buildingSelectedCityOption}
            onChange={handleBuildingCitySelect}
            options={buildingCityOptions}
            placeholder="Select a city"
            isClearable
            isDisabled={!buildingSelectedDistrictId || citiesForBuildings.length === 0}
          />
        </FilterSection>
        <FilterSection $noBorder>
          <Select
            label="Select Street"
            value={buildingSelectedStreetOption}
            onChange={handleBuildingStreetSelect}
            options={buildingStreetOptions}
            placeholder="Select a street"
            isClearable
            isDisabled={!buildingSelectedCityId || streetsForBuildings.length === 0}
          />
        </FilterSection>
        <FilterSection>
          <Select
            label="Administrative District"
            value={buildingSelectedAdministrativeDistrictOption}
            onChange={handleBuildingAdministrativeDistrictSelect}
            options={buildingAdministrativeDistrictOptions}
            placeholder="Select (optional)"
            isClearable
            isDisabled={
              !buildingSelectedCityId || administrativeDistrictsForBuildings.length === 0
            }
          />
        </FilterSection>
      </FilterFlex>

      {buildingsLoading ? (
        <LoadingSpinner>Loading...</LoadingSpinner>
      ) : !buildingSelectedCountryId ? (
        <EmptyState>Please select a country to view buildings.</EmptyState>
      ) : !buildingSelectedDistrictId ? (
        <EmptyState>Please select a region to view buildings.</EmptyState>
      ) : !buildingSelectedCityId ? (
        <EmptyState>Please select a city to view buildings.</EmptyState>
      ) : !buildingSelectedStreetId ? (
        <EmptyState>Please select a street to view buildings.</EmptyState>
      ) : !buildings || buildings.length === 0 ? (
        <EmptyState>No buildings found.</EmptyState>
      ) : (
        <>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>Name</TableHeader>
                <TableHeaderActions>Actions</TableHeaderActions>
              </TableRow>
            </TableHead>
            <TableBody>
              {buildings.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.name}</TableCell>
                  <ActionsCell>
                    <IconButton onClick={() => onEdit(item)}>
                      <EditIcon size={18} color="#2D6CDF" />
                    </IconButton>
                    <IconButton onClick={() => onDelete(item)}>
                      <DeleteIcon size={18} color="#FB2C36" />
                    </IconButton>
                  </ActionsCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {buildingsPagesCount > 1 && (
            <PaginationWrapper>
              <Pagination
                currentPage={buildingsCurrentPage}
                totalPages={buildingsPagesCount}
                onPageChange={handlePageChange}
                handleRowCountChange={handleRowCountChange}
                dataCount={buildings.length}
                count={buildingsSearchData.limit}
                editableRowCount
              />
            </PaginationWrapper>
          )}
        </>
      )}
    </>
  );
};

export default BuildingPage;
